/*
  Bonus programme storefront client.

  Talks to the bonus service through the Shopify app proxy at /apps/bonus/*.
  The proxy signs every request and passes logged_in_customer_id, so the page
  never sends a customer id itself: whatever the browser claims is ignored.

  Custom elements:
    <bonus-balance>  fills balance and tier figures once the request resolves
    <bonus-redeem>   redemption control in the cart and the cart drawer
*/
(() => {
  const PROXY = '/apps/bonus';
  const EVENT_PREFIX = 'cg:';

  function track(name, params = {}) {
    if (window.Shopify?.designMode) return;
    window.Shopify?.analytics?.publish(EVENT_PREFIX + name, params);
  }

  /* Balance is requested once per page and shared by every element on it. */
  let balancePromise = null;

  function fetchBalance() {
    if (!balancePromise) {
      balancePromise = fetch(`${PROXY}/balance`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
    }
    return balancePromise;
  }

  async function post(path, body) {
    const response = await fetch(`${PROXY}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, data };
  }

  /* Cart contents are advisory only: the service re-checks the cap through the
     minimum subtotal on the discount code it issues. Promo codes, on the other
     hand, decide whether redemption is offered at all — bonuses do not stack
     with them, while the store-wide automatic sale is fine. */
  async function cartState() {
    try {
      const cart = await fetch(`${window.routes?.cart_url || '/cart'}.js`).then((r) => r.json());

      const fromCodes = (cart.discount_codes || []).filter((entry) => entry.applicable !== false).map((entry) => entry.code);
      const fromApplications = (cart.cart_level_discount_applications || [])
        .filter((application) => application.type === 'discount_code')
        .map((application) => application.title);

      return {
        items: cart.items.map((item) => ({
          variant_id: item.variant_id,
          final_line_price: item.final_line_price,
          gift_card: Boolean(item.gift_card),
        })),
        promo_codes: [...new Set([...fromCodes, ...fromApplications])].filter(Boolean),
      };
    } catch {
      return { items: [], promo_codes: [] };
    }
  }

  const money = (satang, format) => {
    const amount = (satang / 100).toLocaleString(document.documentElement.lang || 'en', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return (format || '{{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, amount);
  };

  class BonusBalance extends HTMLElement {
    connectedCallback() {
      this.format = this.dataset.moneyFormat;
      fetchBalance().then((data) => this.render(data));
    }

    render(data) {
      const customer = data?.customer;
      if (!customer) {
        this.hidden = true;
        return;
      }

      this.fill('spendable', customer.balance.spendable);
      this.fill('pending', customer.balance.pending);
      this.fill('remaining', customer.progress?.remaining ?? 0);

      const expiry = this.querySelector('[data-bonus-expiry]');
      if (expiry && customer.balance.nextExpiry) {
        expiry.hidden = false;
        const date = new Date(customer.balance.nextExpiry.at);
        expiry.querySelector('[data-bonus-expiry-amount]').textContent = money(customer.balance.nextExpiry.amount, this.format);
        expiry.querySelector('[data-bonus-expiry-date]').textContent = date.toLocaleDateString(
          document.documentElement.lang || 'en',
          { day: 'numeric', month: 'long', year: 'numeric' },
        );
      }

      const meter = this.querySelector('[data-bonus-progress-value]');
      if (meter) meter.style.setProperty('--bonus-progress', `${customer.progress?.percent ?? 0}%`);

      this.querySelectorAll('[data-bonus-pending-wrapper]').forEach((node) => {
        node.hidden = customer.balance.pending <= 0;
      });

      this.classList.add('bonus-balance--ready');
      this.dataset.tier = customer.tier.code;

      track('bonus_balance_viewed', {
        bonus_tier: customer.tier.code,
        value: customer.balance.spendable / 100,
        currency: window.Shopify?.currency?.active,
      });
    }

    fill(name, satang) {
      this.querySelectorAll(`[data-bonus-${name}]`).forEach((node) => {
        node.textContent = money(satang, this.format);
      });
    }
  }

  class BonusRedeem extends HTMLElement {
    connectedCallback() {
      this.format = this.dataset.moneyFormat;
      this.slider = this.querySelector('[data-bonus-slider]');
      this.output = this.querySelector('[data-bonus-amount]');
      this.applyButton = this.querySelector('[data-bonus-apply]');
      this.status = this.querySelector('[data-bonus-status]');
      this.body = this.querySelector('[data-bonus-body]');

      this.slider?.addEventListener('input', () => this.onSlide());
      this.applyButton?.addEventListener('click', () => this.apply());

      this.load();
    }

    async load() {
      const quote = await post('/quote', await cartState());
      if (!quote.ok || !quote.data?.enabled) {
        this.hidden = true;
        return;
      }

      this.quote = quote.data;
      const max = quote.data.max_redeemable;

      if (max <= 0) {
        this.showReason(quote.data);
        return;
      }

      this.hidden = false;
      this.body.hidden = false;
      this.slider.min = 0;
      this.slider.max = max;
      this.slider.step = quote.data.step;
      this.slider.value = max;
      this.onSlide();

      this.fillMax(max);
      track('bonus_redeem_available', { value: max / 100, currency: window.Shopify?.currency?.active });
    }

    fillMax(max) {
      this.querySelectorAll('[data-bonus-max]').forEach((node) => {
        node.textContent = money(max, this.format);
      });
    }

    showReason(quote) {
      // Пустой блок лучше не показывать: он только отвлекает в корзине
      const reasons = {
        order_below_minimum: '[data-bonus-reason-minimum]',
        promo_code_present: '[data-bonus-reason-promo]',
        no_balance: null,
        below_step: null,
      };
      const selector = reasons[quote.reason];
      const node = selector ? this.querySelector(selector) : null;

      // Списание предложено, но недоступно: причина показывает, что мешает покупателю.
      track('bonus_redeem_blocked', { reason: quote.reason || 'unknown' });

      if (!node) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      node.hidden = false;
      node.querySelectorAll('[data-bonus-minimum]').forEach((el) => {
        el.textContent = money(quote.min_order, this.format);
      });
    }

    onSlide() {
      const value = Number(this.slider.value);
      this.output.textContent = money(value, this.format);
      this.applyButton.disabled = value <= 0;
    }

    async apply() {
      const amount = Number(this.slider.value);
      if (amount <= 0) return;

      this.applyButton.disabled = true;
      this.setStatus(this.dataset.textApplying);

      const result = await post('/reserve', { amount, ...(await cartState()) });

      if (!result.ok || !result.data?.code) {
        this.setStatus(this.dataset.textError);
        this.applyButton.disabled = false;
        track('bonus_redeem_error', { reason: result.data?.reason || 'request_failed', value: amount / 100 });
        return;
      }

      track('bonus_redeem_applied', { value: amount / 100, currency: window.Shopify?.currency?.active });

      // Код применяется штатной ссылкой Shopify: она кладёт скидку в корзину
      // и возвращает покупателя обратно.
      window.location.href = `/discount/${encodeURIComponent(result.data.code)}?redirect=${encodeURIComponent(
        window.routes?.cart_url || '/cart',
      )}`;
    }

    setStatus(text) {
      if (this.status && text) this.status.textContent = text;
    }
  }

  /* The earn hint is rendered server-side but stays hidden until the service
     confirms the programme is actually running: the theme setting only says
     the programme is dressed up, not that anything is being accrued. */
  function revealEarnHints(root = document) {
    const hints = root.querySelectorAll('[data-bonus-earn][hidden]');
    if (hints.length === 0) return;

    fetchBalance().then((data) => {
      if (!data?.programme?.enabled) return;
      hints.forEach((hint) => hint.removeAttribute('hidden'));
    });
  }

  if (!customElements.get('bonus-balance')) customElements.define('bonus-balance', BonusBalance);
  if (!customElements.get('bonus-redeem')) customElements.define('bonus-redeem', BonusRedeem);

  document.addEventListener('DOMContentLoaded', () => revealEarnHints());

  // Блок цены переставляется при смене варианта — подсказку нужно раскрыть заново
  document.addEventListener('variant:changed', () => revealEarnHints());
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType === 1 && node.querySelector?.('[data-bonus-earn][hidden]')) {
          revealEarnHints(node);
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
